import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, errText } from "@/lib/api";
import { LEAD_STATUSES, STATUS_STYLES, QUAL_STYLES, Badge, fmtDateTime } from "@/lib/leads";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, FilePlus2, ExternalLink, CheckCircle2, Wrench, XCircle } from "lucide-react";

export const Card = ({ title, children, testId }) => (
  <div className="bg-white border border-gray-200 p-5 sm:p-6" data-testid={testId}>
    {title && <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-4">{title}</p>}
    {children}
  </div>
);

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 text-sm py-2 border-b border-gray-100 last:border-0">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="font-medium text-right" data-testid={testId}>{value || "—"}</span>
  </div>
);

export const LeadPhotos = ({ photos }) => (
  <Card title={`Uploaded photos (${photos.length})`} testId="lead-photos">
    {photos.length === 0 ? (
      <p className="text-sm text-gray-400">No photos were uploaded.</p>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {photos.map((p) => (
          <a key={p.id} href={`${API}/photos/${p.id}`} target="_blank" rel="noreferrer" className="block relative group" data-testid={`lead-photo-${p.id}`}>
            <img src={`${API}/photos/${p.id}`} alt="Customer furniture" loading="lazy" className="w-full aspect-square object-cover bg-gray-100" />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-150" />
          </a>
        ))}
      </div>
    )}
  </Card>
);

export const LeadProject = ({ lead }) => (
  <Card title="Furniture & project" testId="lead-project">
    <Row label="Furniture" value={lead.furniture_types.join(", ") + (lead.furniture_other ? ` (${lead.furniture_other})` : "")} testId="lead-furniture" />
    <Row label="Colour goal" value={lead.colour_goal_label} testId="lead-colour-goal" />
    <Row label="Concerns" value={(lead.concerns || []).join(", ") + (lead.concerns_other ? ` (${lead.concerns_other})` : "")} testId="lead-concerns" />
    <Row label="Timeline" value={lead.timeline_label} testId="lead-timeline" />
    {lead.notes && (
      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">Customer notes</p>
        <p className="text-sm leading-relaxed bg-[#FAF7F2] p-4 border-l-2 border-[#c19a6b]" data-testid="lead-notes">{lead.notes}</p>
      </div>
    )}
  </Card>
);

export const LeadCustomer = ({ lead }) => {
  const c = lead.customer;
  return (
    <Card title="Customer" testId="lead-customer">
      <p className="font-display text-2xl">{c.first_name} {c.last_name}</p>
      <div className="mt-4 space-y-2 text-sm">
        <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-[#8a6a42] hover:text-black transition-colors duration-150" data-testid="lead-email"><Mail size={14} /> {c.email}</a>
        <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-[#8a6a42] hover:text-black transition-colors duration-150" data-testid="lead-phone"><Phone size={14} /> {c.phone}</a>
        <p className="text-gray-600" data-testid="lead-postal">Postal code: <span className="font-medium text-black">{c.postal_code || "—"}</span></p>
      </div>
    </Card>
  );
};

export const LeadSource = ({ lead }) => {
  const u = lead.utm || {};
  return (
    <Card title="Source & attribution" testId="lead-source">
      <Row label="Lead source" value={lead.source === "landing_page" ? "Revival landing page" : lead.source} />
      <Row label="utm_source" value={u.utm_source} testId="lead-utm-source" />
      <Row label="utm_medium" value={u.utm_medium} />
      <Row label="utm_campaign" value={u.utm_campaign} testId="lead-utm-campaign" />
      <Row label="utm_content" value={u.utm_content} />
      <Row label="utm_term" value={u.utm_term} />
      <Row label="Meta click id" value={u.fbclid ? `${u.fbclid.slice(0, 18)}…` : ""} />
      <Row label="Received" value={fmtDateTime(lead.created_at)} />
      <Row label="Last updated" value={fmtDateTime(lead.updated_at)} />
      {lead.landing_url && <p className="text-[11px] text-gray-400 mt-3 break-all">{lead.landing_url}</p>}
      {lead.referrer && <p className="text-[11px] text-gray-400 mt-1 break-all">Referrer: {lead.referrer}</p>}
    </Card>
  );
};

export const LeadActions = ({ lead, onUpdate }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const c = lead.customer;

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/revival-leads/${lead.id}`, body);
      onUpdate(data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const contact = (href) => {
    window.open(href, "_self");
    if (["New Lead", "Reviewing Photos"].includes(lead.status)) patch({ status: "Contacted" }, "Marked as Contacted");
  };

  const createQuote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/revival-leads/${lead.id}/convert`);
      toast.success(data.existing ? "Opening the existing quote" : "Quote created — finish pricing and send it");
      navigate(`/admin/quotes/${data.quote_id}`);
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };

  const qualBtn = (value, Icon, label, testId) => (
    <Button data-testid={testId} disabled={busy || lead.qualification === value} onClick={() => patch({ qualification: value }, `Marked as ${value}`)} variant="outline" className="rounded-sm h-10 text-[11px] uppercase tracking-wider justify-start">
      <Icon size={13} className="mr-2" /> {label}
    </Button>
  );

  return (
    <Card title="Status & actions" testId="lead-actions">
      <div className="flex flex-wrap gap-2 mb-5">
        <Badge value={lead.qualification} styles={QUAL_STYLES} testId="lead-qualification-badge" />
        <Badge value={lead.status} styles={STATUS_STYLES} testId="lead-status-badge" />
      </div>
      <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500 mb-2">Sales status</p>
      <Select value={lead.status} onValueChange={(v) => patch({ status: v }, `Status: ${v}`)}>
        <SelectTrigger data-testid="lead-status-select" className="h-11 rounded-sm"><SelectValue /></SelectTrigger>
        <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>

      <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500 mt-6 mb-2">Qualification</p>
      <div className="grid gap-2">
        {qualBtn("Revival Candidate", CheckCircle2, "Mark Revival Candidate", "mark-revival-btn")}
        {qualBtn("Potential Restoration Lead", Wrench, "Mark Restoration Lead", "mark-restoration-btn")}
        {qualBtn("Not Suitable", XCircle, "Mark Not Suitable", "mark-not-suitable-btn")}
      </div>

      <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500 mt-6 mb-2">Contact customer</p>
      <div className="grid grid-cols-3 gap-2">
        <Button data-testid="contact-email-btn" variant="outline" className="rounded-sm h-10 text-[11px] uppercase tracking-wider" onClick={() => contact(`mailto:${c.email}?subject=${encodeURIComponent("Your ARK Furniture Revival assessment")}`)}><Mail size={13} className="mr-1.5" /> Email</Button>
        <Button data-testid="contact-call-btn" variant="outline" className="rounded-sm h-10 text-[11px] uppercase tracking-wider" onClick={() => contact(`tel:${c.phone}`)}><Phone size={13} className="mr-1.5" /> Call</Button>
        <Button data-testid="contact-text-btn" variant="outline" className="rounded-sm h-10 text-[11px] uppercase tracking-wider" onClick={() => contact(`sms:${c.phone}`)}><MessageSquare size={13} className="mr-1.5" /> Text</Button>
      </div>

      <div className="mt-6">
        {lead.quote_id ? (
          <Button data-testid="open-quote-btn" onClick={() => navigate(`/admin/quotes/${lead.quote_id}`)} className="w-full h-12 bg-black hover:bg-black/85 text-white rounded-sm text-[11px] uppercase tracking-wider btn-lift">
            <ExternalLink size={14} className="mr-2" /> Open quote
          </Button>
        ) : (
          <Button data-testid="create-quote-from-lead-btn" disabled={busy} onClick={createQuote} className="w-full h-12 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm text-[11px] uppercase tracking-wider btn-lift">
            <FilePlus2 size={14} className="mr-2" /> Create quote from this lead
          </Button>
        )}
      </div>
    </Card>
  );
};

export const LeadAdminNotes = ({ lead, onUpdate }) => {
  const [notes, setNotes] = useState(lead.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/revival-leads/${lead.id}`, { admin_notes: notes });
      onUpdate(data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card title="Internal notes" testId="lead-admin-notes">
      <Textarea data-testid="lead-admin-notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Assessment notes, call summary, next step…" className="rounded-sm" />
      <Button data-testid="lead-admin-notes-save" onClick={save} disabled={saving || notes === (lead.admin_notes || "")} variant="outline" className="rounded-sm h-10 text-[11px] uppercase tracking-wider mt-3">
        {saving ? "Saving…" : "Save notes"}
      </Button>
    </Card>
  );
};
